import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  DateRangeOption,
  getMembershipIntervalDisplaySingle,
  isSubMonthlyMembershipInterval,
  getMembershipPurchaseTypeDisplay,
  getMembershipStatusDisplay,
  Membership,
  MembershipEntryTimeSlot
} from '../../../../../../models/membership';
import { AccountService } from '../../../../../../services/account/account.service';
import { MembershipService } from '../../../../../../services/membership/membership.service';
import { TaxRate } from '../../../../../../models/tax-rate';
import { TaxRateService } from '../../../../../../services/tax-rate/tax-rate.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { EventType, EventTypeCategory } from '../../../../../../models/event-type';
import {
  dateStringFromISO8601,
  getRandomNegativeId,
  propExists,
  scrollToElement
} from '../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { GuestPassEligibility } from '../../../../../../models/customer';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MyAccount } from '../../../../../../models/my-account';
import { LightspeedCustomerGroup, SquareCustomerGroup } from '../../../../../../models/organization-config';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { EventTypeStatus } from '../../../../../../models/event-type-enums';
import { CapitanLocation } from '../../../../../../models/location';
import { SelectLocationModalComponent } from '../../../modals/select-location-modal/select-location-modal.component';
import { LocationService } from '../../../../../../services/location/location.service';
import { AddDateRangeOptionModalComponent } from './modals/add-date-range-option-modal/add-date-range-option-modal.component';
import {
  MembershipDiscountType,
  MembershipInterval,
  MembershipPurchaseType,
  MembershipStatus,
  MembershipVisibility
} from '../../../../../../models/membership-enums';
import { OrgPOSSoftware } from '../../../../../../models/organization-config-enums';
import {
  ensureGroupTierDefaults,
  usesSpecificAllthenticateProducts
} from '../../../../../../utils-models';

export enum VoucherType {
  none,
  eventType,
  eventCategory
}

@Component({
  selector: 'app-membership-detail',
  templateUrl: './membership-detail.component.html',
  styleUrls: ['./membership-detail.component.css']
})
export class MembershipDetailComponent implements OnInit, ComponentHasUnsavedChanges {

  public isNewMembership: boolean;
  public membership: Membership = {
    id: null,
    url: null,
    name: '',
    purchase_type: MembershipPurchaseType.recurring,
    is_group: false,
    interval: MembershipInterval.monthly,
    has_fixed_date_ranges: false,
    duration_years: null,
    duration_months: 1,
    duration_days: null,
    date_range_options: [],
    upfront_payment_options: [],
    upfront_payment_climber_app_text: '',
    status: MembershipStatus.draft,
    description: '',
    confirmation_text: '',
    payment_information_text: '',
    recurring_payment_confirmation_text: '',
    failed_payment_notification_text: '',
    terms_and_conditions_checkbox_text: '',
    membership_management_info_alert_text: '',
    visibility: MembershipVisibility.public,
    is_new_members_only: false,
    max_benefit_grants_per_customer: null,
    is_single_location: false,
    is_location_restricted: false,
    allowed_locations: [],
    num_new_customer_guest_passes: 0,
    new_customer_guest_pass_frequency: MembershipInterval.monthly,
    num_lapsed_customer_guest_passes: 0,
    lapsed_customer_guest_pass_frequency: MembershipInterval.monthly,
    num_all_customer_guest_passes: 0,
    all_customer_guest_pass_frequency: MembershipInterval.monthly,
    voucher_type_id: null,
    voucher_type_name: null,
    voucher_type_status: null,
    voucher_category_id: null,
    voucher_category_name: null,
    num_vouchers: 1,
    allow_freezing: false,
    allow_individual_freezing: false,
    cancel_membership_when_maximum_freeze_reached: false,
    allow_cancellation: false,
    cancellation_date_range_start: null,
    cancellation_date_range_end: null,
    allow_selecting_start_date: false,
    bill_day_of_month: null,
    has_limited_entry: false,
    sends_allthenticate_invite: false,
    membership_fee: null,
    freeze_fee: 0,
    has_youth_tier: false,
    has_fixed_freeze_fee: false,
    discount_type: MembershipDiscountType.currency,
    discounts_are_incremental: false,
    discounts_apply_to_individually_frozen_members: false,
    adult_tier_name: 'Adult',
    youth_tier_name: 'Youth',
    adult_min_age: 18,
    adult_base_fee: null,
    youth_base_fee: null,
    adult_freeze_fee: 0,
    youth_freeze_fee: 0,
    adult_discounts: [
      {
        number_in_tier: 1,
        discount_amount: 0
      },
      {
        number_in_tier: 2,
        discount_amount: 0
      }
    ],
    youth_discounts: [
      {
        number_in_tier: 1,
        discount_amount: 0
      },
      {
        number_in_tier: 2,
        discount_amount: 0
      }
    ],
    allow_unlimited_adults: false,
    allow_unlimited_youths: false,
    minimum_num_members: 2,
    minimum_num_adults: 0,
    minimum_num_youths: 0,
    initiation_fee: 0,
    location_specific_pricing_matrices: [],
    tax_rate_id: null,
    tax_rate_name: null,
    tax_rate_initiation_fee_id: null,
    tax_rate_initiation_fee_name: null,
    tax_rate_per_location: {},
    tax_rate_initiation_fee_per_location: {},
    revenue_account_codes: {},
    per_customer_add_ons: [],
    icon_color_hex: '',
    icon_initials: '',
    allow_purchasing_for_unlinked_customers: true,
    allow_purchase_without_login: true,
    require_staff_approval: false,
    is_youth_programming: false,
    rolling_event_type_id: null,
    mailchimp_scheduled_to_cancel_event_name: '',
    mailchimp_cancelled_event_name: '',
    mailchimp_purchased_by_customer_event_name: '',
    mailchimp_purchased_for_customer_event_name: '',
    mailchimp_active_member_tag_name: '',
    mailchimp_active_membership_owner_tag_name: '',
    member_square_customer_group_id: null,
    member_square_customer_group_name: null,
    member_lightspeed_customer_group_id: null,
    member_lightspeed_customer_group_name: null,
    order_number: 0,
    created_at: null,
    updated_at: null,
    last_updated_by_id: null,
    last_updated_by_first_name: null,
    last_updated_by_last_name: null,
    last_updated_by_email: null,
    entry_time_slots_url: null
  };
  public entryTimeSlots: MembershipEntryTimeSlot[] = [];
  public entryTimeSlotUrlsToDelete: string[] = [];
  public voucherType = VoucherType.none;
  public hasIcon = false;

  // If this is an existing membership, store the existing status to determine
  // whether changing "Draft-edit-only" fields is allowed. We cannot simply
  // read the status of the membership object because then any restricted
  // actions would take effect as soon as the status is changed locally, rather
  // than upon saving.
  public existingStatus: MembershipStatus;
  public hasPerLocationTaxRates = false;
  public hasPerLocationInitiationFeeTaxRates = false;

  public errorMessage = '';
  public membershipErrors: any = {};
  public entryTimeSlotErrors: {[key: number]: any} = {};
  public membershipNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;
  public unsavedVoucherChanges = false;

  public taxRates: TaxRate[];
  public eventTypes: EventType[];
  public eventCategories: EventTypeCategory[];
  public squareCustomerGroups: SquareCustomerGroup[];
  public lightspeedCustomerGroups: LightspeedCustomerGroup[];

  public loadingExistingMembership = false;
  public loadingExistingEntryTimeSlots = false;
  public loadingSubmit = false;
  public loadingTaxRates = true;
  public loadingEventTypes = true;
  public loadingEventCategories = true;
  public loadingSquareCustomerGroups = true;
  public loadingLightspeedCustomerGroups = true;

  public currentSectionId = 'setup';

  public myAccount: MyAccount;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public readonly MembershipPurchaseType = MembershipPurchaseType;
  public readonly MembershipInterval = MembershipInterval;
  public readonly GuestPassEligibility = GuestPassEligibility;
  public readonly MembershipVisibility = MembershipVisibility;
  public readonly getMembershipStatusDisplay = getMembershipStatusDisplay;
  public readonly getMembershipPurchaseTypeDisplay = getMembershipPurchaseTypeDisplay;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly EventTypeStatus = EventTypeStatus;
  public readonly OrgPOSSoftware = OrgPOSSoftware;
  public readonly VoucherType = VoucherType;
  public readonly scrollToElement = scrollToElement;
  public readonly getMembershipIntervalDisplaySingle = getMembershipIntervalDisplaySingle;
  public readonly isSubMonthlyMembershipInterval = isSubMonthlyMembershipInterval;

  // Accessed via getLocations()
  private locations: CapitanLocation[] = null;

  constructor(
    public accountService: AccountService,
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private locationService: LocationService,
    private membershipService: MembershipService,
    private taxRateService: TaxRateService,
    private eventTypeService: EventTypeService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewMembership = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    // Get the membership to edit, if an ID was provided.
    if (!this.isNewMembership) {
      const membershipIdInt = parseInt(routeParams.id, 10);
      if (isNaN(membershipIdInt)) {
        this.membershipNotFound = true;
        this.loadingExistingMembership = false;
        return;
      }

      this.loadMembership(membershipIdInt);
      this.loadEntryTimeSlots(membershipIdInt);
    }

    this.loadTaxRates();
    this.loadEventTypes();
    this.loadEventCategories();
    this.loadSquareCustomerGroups();
    this.loadLightspeedCustomerGroups();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Load the entry time slots for the membership with the given ID (used for
   * editing existing memberships).
   */
  public loadEntryTimeSlots(membershipId: number) {
    this.loadingExistingEntryTimeSlots = true;

    this.membershipService
      .getMembershipEntryTimeSlots(membershipId, 1, 100)
      .then(entryTimeSlotsResponse => {
        this.entryTimeSlots = entryTimeSlotsResponse.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.membershipNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingEntryTimeSlots = false; });
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRates = response.results;

        // Set the initial tax rate to the first one in the list, if the
        // membership does not already have a tax rate. If this loads before the
        // membership does, that's okay because the membership will be
        // overwritten.
        if (this.membership.tax_rate_id === null) {
          if (this.taxRates.length > 0) {
            this.membership.tax_rate_id = this.taxRates[0].id;
          }
        }
        if (this.membership.tax_rate_initiation_fee_id === null) {
          if (this.taxRates.length > 0) {
            this.membership.tax_rate_initiation_fee_id = this.taxRates[0].id;
          }
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  /**
   * Get the list of available statuses for the membership. This enforces the
   * rule that a membership may not be reverted to Draft once it has become
   * Active or Retired.
   */
  public getAvailableStatuses(): MembershipStatus[] {
    if (this.isExistingNonDraftMembership()) {
      return [MembershipStatus.active, MembershipStatus.retired];
    } else {
      return [MembershipStatus.draft, MembershipStatus.active, MembershipStatus.retired];
    }
  }

  /**
   * Whether the membership is an existing, non-draft membership.
   * This determines whether certain basic fields can be edited.
   */
  public isExistingNonDraftMembership(): boolean {
    return !this.isNewMembership && (this.existingStatus !== MembershipStatus.draft);
  }

  public addDateRangeOption() {
    const modalRef = this.modalService.open(AddDateRangeOptionModalComponent);
    modalRef.componentInstance.completedCallback = (result: DateRangeOption) => {
      this.membership.date_range_options.push(result);
      this.unsavedChanges = true;
    }
  }

  public onSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.membershipErrors = {};
    this.entryTimeSlotErrors = {};

    if (this.voucherType === VoucherType.none) {
      this.membership.voucher_type_id = null;
      this.membership.voucher_category_id = null;
      this.membership.num_vouchers = null;
    } else if (this.voucherType === VoucherType.eventType) {
      this.membership.voucher_category_id = null;
    } else if (this.voucherType === VoucherType.eventCategory) {
      this.membership.voucher_type_id = null;
    }

    // Replace blank discounts before saving.
    if (this.membership.is_group) {
      for (const adultDiscount of this.membership.adult_discounts) {
        // @ts-ignore
        if (!propExists(adultDiscount.discount_amount) || (adultDiscount.discount_amount === '')) {
          adultDiscount.discount_amount = 0;
        }
      }
      if (this.membership.has_youth_tier) {
        for (const youthDiscount of this.membership.youth_discounts) {
          // @ts-ignore
          if (!propExists(youthDiscount.discount_amount) || (youthDiscount.discount_amount === '')) {
            youthDiscount.discount_amount = 0;
          }
        }
      }
    }

    if (!this.hasPerLocationTaxRates) {
      this.membership.tax_rate_per_location = {};
    }
    if (!this.hasPerLocationInitiationFeeTaxRates) {
      this.membership.tax_rate_initiation_fee_per_location = {};
    }

    if (!this.hasIcon) {
      this.membership.icon_color_hex = '';
      this.membership.icon_initials = '';
    }

    // The date picker emits '' when cleared; the API needs null.
    if (this.membership.cancellation_date_range_start === '') {
      this.membership.cancellation_date_range_start = null;
    }
    if (this.membership.cancellation_date_range_end === '') {
      this.membership.cancellation_date_range_end = null;
    }

    // Save the membership.
    let membershipSavePromise: Promise<Membership>;
    if (this.isNewMembership) {
      membershipSavePromise = this.membershipService.addMembership(this.membership);
    } else {
      membershipSavePromise = this.membershipService.updateMembership(this.membership);
    }

    membershipSavePromise
      .then(savedMembership => {
        this.membership = savedMembership;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.membershipErrors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        }

        return Promise.reject(reason);
      })
      .then(() => {
        return this.saveTimeSlots();
      })
      .then(() => {
        this.unsavedChanges = false;
        this.unsavedVoucherChanges = false;

        let successMessage: string;
        if (this.isNewMembership) {
          successMessage = this.membership.name + ' created successfully.';
        } else {
          successMessage = this.membership.name + ' updated successfully.';
        }

        this.router.navigate(
          ['/memberships-and-passes'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          // Do nothing: this is handled in the individual promises.
        } else if (reason.status === 403) {
          if (this.isNewMembership) {
            this.errorMessage = 'You do not have permission to add memberships.';
          } else {
            this.errorMessage = 'You do not have permission to modify this membership.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public removeEntryTimeSlot(timeSlot: MembershipEntryTimeSlot) {
    // If the entry time slot has a positive ID, then it is present in the API
    // and must be deleted.
    if (timeSlot.id > 0) {
      this.entryTimeSlotUrlsToDelete.push(timeSlot.url);
    }

    this.entryTimeSlots.splice(
      this.entryTimeSlots.indexOf(timeSlot),
      1
    );
  }

  /**
   * Whether all days are available for the given time slot.
   */
  public allDaysAreAvailable(timeSlot: MembershipEntryTimeSlot) {
    return (
      timeSlot.is_available_sunday &&
      timeSlot.is_available_monday &&
      timeSlot.is_available_tuesday &&
      timeSlot.is_available_wednesday &&
      timeSlot.is_available_thursday &&
      timeSlot.is_available_friday &&
      timeSlot.is_available_saturday
    );
  }

  /**
   * Set all days to be available or unavailable for the given time slot.
   */
  public setAllDaysAvailability(timeSlot: MembershipEntryTimeSlot, isAvailable: boolean) {
    timeSlot.is_available_sunday = isAvailable;
    timeSlot.is_available_monday = isAvailable;
    timeSlot.is_available_tuesday = isAvailable;
    timeSlot.is_available_wednesday = isAvailable;
    timeSlot.is_available_thursday = isAvailable;
    timeSlot.is_available_friday = isAvailable;
    timeSlot.is_available_saturday = isAvailable;
  }

  /**
   * Add a new entry time slot for the membership.
   */
  public addNewEntryTimeSlot() {
    this.entryTimeSlots.push({
      id: getRandomNegativeId(),
      url: '',
      start_time: '',
      end_time: '',
      is_available_sunday: false,
      is_available_monday: false,
      is_available_tuesday: false,
      is_available_wednesday: false,
      is_available_thursday: false,
      is_available_friday: false,
      is_available_saturday: false,
      created_at: '',
      updated_at: ''
    });
  }

  public usesSpecificAllthenticateProducts(): boolean {
    return usesSpecificAllthenticateProducts(
      this.orgConfig.getOrganizationConfig()
    );
  }

  public handleGroupTierChange() {
    ensureGroupTierDefaults(this.membership);
  }

  /**
   * Whether the membership's selected voucher event category is removed
   * (soft-deleted), i.e. absent from the active-category list. Drives the
   * pinned "<name> (Removed)" option so the select isn't blank (PRO-1310);
   * follows the `taxRateRemoved` convention in the Pricing section.
   */
  public eventCategoryRemoved(eventCategoryId: number): boolean {
    if ((eventCategoryId === null) || (eventCategoryId === undefined)) {
      return false;
    }
    if (!this.eventCategories) { return true; }

    for (const eventCategory of this.eventCategories) {
      if (eventCategory.id === eventCategoryId) { return false; }
    }

    return true;
  }

  public resyncSquareCustomerGroups() {
    const orgConfigObj = this.orgConfig.getOrganizationConfig();
    if ((orgConfigObj === null) || (orgConfigObj.pos_software !== OrgPOSSoftware.square)) {
      return;
    }

    this.loadingSquareCustomerGroups = true;

    this.orgConfig
      .syncSquareCustomerGroups()
      .then(() => {
        return this.loadSquareCustomerGroups();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingSquareCustomerGroups = false; });
  }

  public resyncLightspeedCustomerGroups() {
    const orgConfigObj = this.orgConfig.getOrganizationConfig();
    if ((orgConfigObj === null) || (orgConfigObj.pos_software !== OrgPOSSoftware.lightspeed)) {
      return;
    }

    this.loadingLightspeedCustomerGroups = true;

    this.orgConfig
      .syncLightspeedCustomerGroups()
      .then(() => {
        return this.loadLightspeedCustomerGroups();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingLightspeedCustomerGroups = false; });
  }

  public eventTypeIdInList(eventTypeId: number) {
    for (const eventType of this.eventTypes) {
      if (eventType.id === eventTypeId) { return true; }
    }
    return false;
  }

  public onSectionChange(sectionId: string) {
    this.currentSectionId = sectionId;
  }

  public openAddAllowedLocationModal() {
    const modalRef = this.modalService.open(SelectLocationModalComponent);
    modalRef.componentInstance.excludeIds = this.membership
      .allowed_locations.map(loc => loc.id);
    modalRef.componentInstance.submitCallback = (result: CapitanLocation) => {
      this.membership.allowed_locations.push({
        id: result.id,
        name: result.name
      });
      this.unsavedChanges = true;
    }
  }

  public getLocations(): CapitanLocation[] {
    if (!this.locations) {
      this.locations = this.locationService.getLocations();
    }
    return this.locations;
  }

  public getIsGroupReasonDisabled() {
    if (this.isExistingNonDraftMembership()) {
      return 'May not be changed for an existing non-draft membership.';
    } else if (this.membership.purchase_type === MembershipPurchaseType.benefit) {
      return 'Benefit memberships are always individual.';
    } else {
      return null;
    }
  }

  public getVisibilityReasonDisabled() {
    if (this.membership.purchase_type === MembershipPurchaseType.benefit) {
      return 'Benefit memberships are never available for sale in the Climber App.';
    } else {
      return null;
    }
  }

  public handlePurchaseTypeChange() {
    if (this.membership.purchase_type === MembershipPurchaseType.benefit) {
      this.membership.is_group = false;
      this.membership.visibility = MembershipVisibility.staffOnly;
    } else {
      this.membership.max_benefit_grants_per_customer = null;
    }
  }

  /**
   * Load the membership with the given ID (used for editing existing
   * memberships).
   */
  private loadMembership(membershipId: number) {
    this.loadingExistingMembership = true;

    this.membershipService
      .getMembership(membershipId)
      .then(membership => {
        this.membership = membership;
        this.existingStatus = membership.status;
        if (this.membership.voucher_type_id) {
          this.voucherType = VoucherType.eventType;
        }
        if (this.membership.voucher_category_id) {
          this.voucherType = VoucherType.eventCategory;
        }
        if (Object.keys(this.membership.tax_rate_per_location).length > 0) {
          this.hasPerLocationTaxRates = true;
        }
        if (Object.keys(this.membership.tax_rate_initiation_fee_per_location).length > 0) {
          this.hasPerLocationInitiationFeeTaxRates = true;
        }
        if (this.membership.icon_color_hex !== '') {
          this.hasIcon = true;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.membershipNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingMembership = false; });
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(
        1,
        3000,
        {status: EventTypeStatus.active}
      )
      .then(response => {
        this.eventTypes = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventTypes = false; });
  }

  private loadEventCategories() {
    this.loadingEventCategories = true;

    this.eventTypeService
      .getEventTypeCategories(
        1,
        3000
      )
      .then(response => {
        this.eventCategories = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingEventCategories = false;
      });
  }

  private loadSquareCustomerGroups() {
    this.loadingSquareCustomerGroups = true;

    const orgConfigObj = this.orgConfig.getOrganizationConfig();
    if ((orgConfigObj === null) || (orgConfigObj.pos_software === OrgPOSSoftware.square)) {
      this.orgConfig
        .getSquareCustomerGroups()
        .then(response => {
          this.squareCustomerGroups = response.filter(cg => cg.is_managed_by_capitan);
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.accountService.logout();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingSquareCustomerGroups = false; });
    } else {
      this.squareCustomerGroups = [];
      this.loadingSquareCustomerGroups = false;
    }
  }

  private loadLightspeedCustomerGroups() {
    this.loadingLightspeedCustomerGroups = true;

    const orgConfigObj = this.orgConfig.getOrganizationConfig();
    if ((orgConfigObj === null) || (orgConfigObj.pos_software === OrgPOSSoftware.lightspeed)) {
      this.orgConfig
        .getLightspeedCustomerGroups()
        .then(response => {
          this.lightspeedCustomerGroups = response
            .filter(cg => cg.is_managed_by_capitan)
            .sort((a, b) => a.name < b.name ? -1 : 1);
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.accountService.logout();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingLightspeedCustomerGroups = false; });
    } else {
      this.lightspeedCustomerGroups = [];
      this.loadingLightspeedCustomerGroups = false;
    }
  }

  private saveTimeSlots(): Promise<any>[] {
    const savePromises: Promise<any>[] = [];

    // Save the membership time slots.
    for (const timeSlotToSave of this.entryTimeSlots) {
      let timeSlotSavePromise: Promise<MembershipEntryTimeSlot>;
      if (timeSlotToSave.id > 0) {
        timeSlotSavePromise = this.membershipService.updateMembershipEntryTimeSlot(timeSlotToSave);
      } else {
        timeSlotSavePromise = this.membershipService.addMembershipEntryTimeSlot(this.membership.id, timeSlotToSave);
      }
      timeSlotSavePromise = timeSlotSavePromise.catch(reason => {
        if (reason.status === 400) {
          this.entryTimeSlotErrors[timeSlotToSave.id] = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        }

        return Promise.reject(reason);
      });
      savePromises.push(timeSlotSavePromise);
    }

    // Delete the removed membership time slots.
    for (const timeSlotUrlToDelete of this.entryTimeSlotUrlsToDelete) {
      savePromises.push(
        this.membershipService.deleteMembershipEntryTimeSlot(timeSlotUrlToDelete)
      );
    }

    return savePromises;
  }

}
